import { useEffect, useState, RefObject } from 'react';

import { useWindowIsResizing } from '.';

interface UseExpansionDirectionInput {
    childElementRef?: RefObject<HTMLElement>;
    childElementHeight?: number;
    parentElementRef: RefObject<HTMLElement>;
    refreshRate?: number;
}

/**
 * Given two elements, a parent element and child element, determine which direction the child
 * element should expand in, either 'up' or 'down'. If there is enough room in the viewport to
 * display the child below the parent, do so. If not, but there is enough to display the child above
 * the parent, do that. If neither are true, display the child below the parent.
 *
 * This is useful in components like a dropdown, where the parent element is the dropdown toggle and
 * the child element is the dropdown list. Note that you will need to handle positioning
 * appropriately in your implementation's CSS; i.e. you will need to use a 'relative' parent and
 * 'absolute' child to remove the child from the regular DOM flow, such that when the 'child' is
 * expanded, it does not reposition the elements around it.
 * See packages/components/src/Dropdown/DropdownWrapper.tsx for an example.
 */
export const useExpansionDirection = ({
    childElementRef,
    childElementHeight,
    parentElementRef,
    refreshRate = 1000,
}: UseExpansionDirectionInput): ExpansionDirection => {
    const [expansionDirection, setExpansionDirection] =
        useState<ExpansionDirection>('down');
    const vh = Math.max(
        document.documentElement.clientHeight,
        window.innerHeight || 0,
    );
    const { windowIsResizing } = useWindowIsResizing(refreshRate);

    useEffect((): void => {
        if (!windowIsResizing) {
            const parentRect =
                parentElementRef?.current?.getBoundingClientRect() ?? null;

            if (parentRect !== null) {
                const parentTop = parentRect?.top ?? 0;
                const parentBottom = parentRect?.bottom ?? 0;

                let childHeight = 0;

                if (childElementHeight) {
                    childHeight = childElementHeight;
                } else {
                    const rect =
                        childElementRef?.current?.getBoundingClientRect() ??
                        null;
                    const childTop = rect?.top ?? 0;
                    const childBottom = rect?.bottom ?? 0;

                    childHeight = childTop - childBottom;
                }

                if (parentBottom + childHeight > vh) {
                    if (parentTop - childHeight < 0) {
                        // child element is too tall to fit either above or below parent; revert to
                        // 'down'
                        if (expansionDirection !== 'down') {
                            setExpansionDirection('down');
                        }
                    } else if (expansionDirection !== 'up') {
                        // child element is too tall to fit below parent, but will fit above parent;
                        // ensure that direction is 'up'
                        setExpansionDirection('up');
                    }
                } else if (expansionDirection !== 'down') {
                    // child element will fit below parent; ensure that direction is 'down'
                    setExpansionDirection('down');
                }
            }
        }
    }, [windowIsResizing, parentElementRef.current, vh]);

    return expansionDirection;
};
